import { Component, effect, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Filter, ItemTypeOverride, Presets, PreviewCustomRule, WeaponFilter } from '../models/filter-models';
import { TooltipWrapperComponent } from '../common/tooltip-wrapper/tooltip-wrapper.component';
import { BaseComponent } from '../common/base.component';
import { FilterService } from '../services/filter.service';
import { CosmeticPresetsService } from '../services/cosmetic-presets.service';
import { PresetsService, randomLootAreaLevelForProgressPreset } from '../services/presets.service';
import { ItemDataService } from '../services/item-data.service';
import { BaseType, Tier, UniqueItem } from '../models/item-data-models';
import { AutocompleteComponent } from '../common/autocomplete/autocomplete.component';
import { BulkImportGemsDialogComponent } from '../quick-filter-tab/gem-filters-poe1/bulk-import-gems-dialog/bulk-import-gems-dialog.component';
import { FormsModule } from '@angular/forms';
import { socketGroupKeyDown } from '../common/helper';
import { DEFAULT_FILTER_NAME, poe1 } from '../../game';
import { ArmourType, BaseTypeTier, DefenceType, DisplayType, GearSizeType, MinimapIconShape, Rarity, VendoringGearType, WeaponType, DefaultLabelSize, DEFAULT_LABEL_SIZE_OPTIONS, DEFAULT_LABEL_SIZE_LABELS, EndgameContent, EndgameContentIconMap, EndgameContentLabelMap, ENDGAME_ALL_LABEL } from '../models/enums';
import { CurrencyTierService } from '../services/tiering/currency-tier.service';
import { SelectGearSizeComponent } from '../common/select/select-gear-size/select-gear-size.component';
import { EquipmentType } from '../models/equipment-type.enum';
import { FORCE_FIXED_ECO, LEAGUE_NAME } from '../const';
import { Economy } from '../models/economy-models';
import { UniquesTierService } from '../services/tiering/uniques-tier.service';
import { FileSaveService } from '../services/file-save.service';
import { PresetsHelperService } from '../services/presets-helper.service';
import { STARTER_WEAPON_BASE_TYPES } from '../services/generators/leveling-gear-generator.service';
import { PresetPreviewService } from '../services/preset-preview.service';
import { DropPreviewComponent } from '../common/drop-preview/drop-preview.component';
import { RulePreviewComponent } from '../common/rule-preview/rule-preview.component';
import { DropPreviewSectionVisibility } from '../services/drop-preview.models';
import { RandomLootService, RandomLootSettings } from '../services/random-loot.service';
import { EconomyService } from '../services/economy.service';
import { formatTierThresholdValue } from '../tier-list-tab/tier-value-helpers';
import { ItemLabelComponent } from '../common/item-label/item-label.component';
import { EndgameContentPresetService } from '../services/endgame-content-preset.service';

type StartingTier = 'sTier' | 'aTier' | 'bTier' | 'cTier';

const STARTING_TIER_OPTIONS: StartingTier[] = ['sTier', 'aTier', 'bTier', 'cTier'];
const STARTING_TIER_LABELS: Record<StartingTier, string> = {
  sTier: 'S-Tier',
  aTier: 'A-Tier',
  bTier: 'B-Tier',
  cTier: 'C-Tier',
};

interface EndgameContentInfoDetail {
  contentLine: string;
  itemTypes: { itemType: string; image?: string }[];
  moreItemTypesText?: string;
  promotedLabel?: string;
  uniques: { name: string; image?: string }[];
  moreUniquesText?: string;
  moreBossesText?: string;
}

@Component({
  selector: 'app-presets-tab',
  imports: [TooltipWrapperComponent, DropPreviewComponent, RulePreviewComponent, AutocompleteComponent, BulkImportGemsDialogComponent, FormsModule, SelectGearSizeComponent, ItemLabelComponent,
  ],
  templateUrl: './presets-tab.component.html',
  styleUrls: ['./presets-tab.component.scss', '../common/preset-preview-shared.scss']
})
export class PresetsTabComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) filter!: Filter;
  @Input({ required: true }) initialVisit!: boolean;

  changeVisualStyle = false;
  showTransparentGold = true;
  vendoringRareStyle!: PreviewCustomRule;
  vendoringMagicStyle!: PreviewCustomRule;
  overridePresetTooltip?: string;
  overrideClassPresetTooltip?: string;
  classPresetTexts: { [preset in string]: string };
  areas: Record<string, string> = {};
  showCheapUniques = false;
  bosses = false;
  heist = false;
  chanceBases = false;
  tomes = false;
  shippingUniques = false;
  jewels = true;
  hideETier = false;

  hideLowValueTablets = false;

  maps: BaseType[] = [];
  gemTypes: BaseType[];

  weaponClasses: { name: string, image: string }[] = [];
  defenceTypes: { name: string, image: string }[] = [];

  defaultLabelSizeIndex = 3;
  defaultLabelSizeLabel = DEFAULT_LABEL_SIZE_LABELS.default;
  startingTierIndex = 2;
  startingTierLabel = STARTING_TIER_LABELS.bTier;
  showStartingTierSlider = false;

  bulkImportDialogOpen = false;
  installationInfo = false;
  windowsMoreInfo = false;
  showCosmeticPresets = false;

  defaultFilterName = DEFAULT_FILTER_NAME;
  leagueName = LEAGUE_NAME;

  favouriteEndgameContentMap = EndgameContentIconMap;
  endgameAllLabel = ENDGAME_ALL_LABEL;
  endgameContentPreviewIcons!: string[];
  endgameContentHoverDetail?: EndgameContentInfoDetail;
  endgameContentSelectedDetail?: EndgameContentInfoDetail;
  endgameContentHoveringAll = false;
  isSpecificEndgameContentSelected = false;
  showFavouriteRestrictedBossUniquesToggle = false;
  endgameAllBossUniquesStatusLine = '';
  showEndgamePresetFavouriteCurrencyToggle = false;
  showEndgamePresetCustomRulesToggle = false;
  endgamePresetCustomRuleDescriptions: { contentLabel: string; description: string }[] = [];

  private uniqueByName = new Map<string, UniqueItem>();

  mapTypeOptions: BaseType[] = [];
  selectedMapTypes: string[] = [];

  Rarity = Rarity;
  GearSizeType = GearSizeType;
  VendoringGearType = VendoringGearType;
  DisplayType = DisplayType;
  EquipmentType = EquipmentType;

  eTierCurrency: string[] = [];
  cheapCurrencyWithImages: BaseType[] = [];

  socketGroupKeyDown = socketGroupKeyDown;

  casterWeaponTypes = [WeaponType.Wands, WeaponType.Sceptres, WeaponType.Staves];

  private pendingProgressScroll = false;
  private scrollAnimationFrame?: number;
  private readonly sectionTransitionMs = 500;
  private readonly expansionScrollDistance = 250;

  readonly supportsFileSystemAccess: boolean;
  classPresetOptions: { key: string; img: string }[];

  readonly presetsDropPreviewInitialSections: DropPreviewSectionVisibility = {
    itemLibrary: false,
    randomLoot: false,
    currencyDropTiers: true,
    uniqueDropTiers: true,
    gold: true,
    customCosmeticRules: false,
    customRules: false,
    ...(poe1 ? { divinationCardDropTiers: true } : { divinationCardDropTiers: false }),
  };

  get presetsDropPreviewRandomLootSettings(): RandomLootSettings {
    const preset = this.filter.progressPreset;
    const areaLevel = preset ? randomLootAreaLevelForProgressPreset(preset, this.poe2) : null;
    return RandomLootService.settingsForAreaLevel(areaLevel, false);
  }

  constructor(private filterService: FilterService, private cosmeticPresetsService: CosmeticPresetsService, public presetsService: PresetsService,
    private itemDataService: ItemDataService, private currencyTierService: CurrencyTierService, private uniquesTierService: UniquesTierService,
    private presetHelperService: PresetsHelperService, fileSaveService: FileSaveService, private presetPreviewService: PresetPreviewService,
    private economyService: EconomyService, private endgameContentPresetService: EndgameContentPresetService) {
    super();
    for (const unique of this.itemDataService.getUniquesList()) {
      this.uniqueByName.set(unique.name, unique);
    }
    this.endgameContentPreviewIcons = this.presetsService.endgameContent
      .slice(0, 4)
      .map(c => EndgameContentIconMap[c]);
    this.supportsFileSystemAccess = fileSaveService.supportsFileSystemAccess;
    this.areas = this.itemDataService.getAreas();
    const itemData = this.itemDataService.getItemData();
    this.classPresetTexts = this.itemDataService.getClassPresetTexts();
    this.maps = this.poe2 ? [] : itemData.filter(i => i.itemType === 'Maps').flatMap(i => i.baseTypes);
    this.gemTypes = this.poe2 ? [] : itemData.find(i => i.itemType === 'Skill Gems')!.baseTypes.filter(b => !this.itemDataService.getTransfiguredGems().includes(b.name))
      .concat(itemData.find(i => i.itemType === 'Support Gems')!.baseTypes);
    this.weaponClasses = this.itemDataService.getAllWeaponTypes().map(w => ({ name: w, image: this.itemDataService.getItemData().find(i => i.itemClass === w)!.baseTypes[0].image! }));
    this.defenceTypes = this.itemDataService.getAllDefenceTypes();

    this.cheapCurrencyWithImages = itemDataService.getFlatItemData()
      .filter(i => itemDataService.getCheapCurrency().includes(i.baseType.name))
      .map(i => ({ name: i.baseType.name, image: i.baseType.image }));

    const classNames = this.poe2
      ? ['Warrior', 'Mercenary', 'Ranger', 'Huntress', 'Monk', 'Witch', 'Sorceress', 'Druid']
      : ['Marauder', 'Duelist', 'Ranger', 'Scion', 'Shadow', 'Witch', 'Templar'];
    this.classPresetOptions = classNames.map(key => ({
      key,
      img: `${this.imgPrefix}/img/presets/${key.toLowerCase()}.webp`,
    }));

    this.vendoringRareStyle = new PreviewCustomRule({
      displayOptions: {
        active: true,
        displayType: DisplayType.Custom,
        cosmeticOptions: {
          textColor: 'rgba(254,224,85,0.6)',
          backgroundColor: 'rgba(0, 0, 0, 0.6)'
        }
      },
      rarity: Rarity.Rare,
      exampleName: 'Crude Bow',
    });

    this.vendoringMagicStyle = new PreviewCustomRule({
      displayOptions: {
        active: true,
        displayType: DisplayType.Custom,
        cosmeticOptions: {
          textColor: 'rgba(122,151,254,0.6)',
          backgroundColor: 'rgba(0, 0, 0, 0.6)'
        }
      },
      rarity: Rarity.Magic,
      exampleName: 'Wooden Club',
    });

    effect(() => {
      const pending = this.presetPreviewService.pendingChange();
      if (!pending && this.pendingProgressScroll) {
        this.pendingProgressScroll = false;
        if (this.filter.progressPreset) {
          this.startExpansionScroll();
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']) {
      this.updateDefaultLabelSizeDisplay();
      this.updateStartingTierDisplay();
      this.updateStartingTierSliderVisibility();
      this.setExtraSettingsBasedOnFilterValues();
      this.updateVendoringExampleNames();
      this.updateSelectedEndgameContentDetail();
      if (!this.filter.favouriteEndgameContent.includes('All')) {
        this.syncEndgameContentEffects();
      }

      if (!this.eTierCurrency.length) {
        const eTierCurrencyItemTypes = this.currencyTierService.getTierList().find(t => t.tier === Tier.EVisible)?.itemTypes;
        if (eTierCurrencyItemTypes) {
          this.eTierCurrency = eTierCurrencyItemTypes.flatMap(i => i.baseTypes.map(b => b.name))?.slice(0, 5) ?? [];
        }
      }

      this.mapTypeOptions = [
        { name: 'Nightmare', image: '/img/maps/map-t17.png' },
        { name: 'Nightmare 8-Mod', image: '/img/maps/map-t17.png' },
        { name: 'T16.5', image: '/img/maps/map-memory-influenced.png' },
        { name: 'T16.5 8-Mod', image: '/img/maps/map-memory-influenced.png' },
        { name: 'Blight-Ravaged', image: '/img/maps/map-blight-ravaged.png' },
        { name: 'Blight-Ravaged 8-Mod', image: '/img/maps/map-blight-ravaged.png' },
        { name: 'T16 Influenced', image: '/img/maps/map-influenced.png' },
        { name: 'T16 Blighted', image: '/img/maps/map-blighted.png' },
        { name: 'T16 Delirium', image: '/img/maps/map-delirium.png' },
        { name: 'T16 8-Mod', image: '/img/maps/map-normal.png' },
        { name: 'T16 Normal', image: '/img/maps/map-normal.png' },
        { name: 'T' + this.filter.mapTypesLowTierMinTier + '+ Memory-Influenced', image: '/img/maps/map-memory-influenced.png' },
        { name: 'T' + this.filter.mapTypesLowTierMinTier + '+ Influenced', image: '/img/maps/map-influenced.png' },
        { name: 'T' + this.filter.mapTypesLowTierMinTier + '+ Blighted', image: '/img/maps/map-blighted.png' },
        { name: 'T' + this.filter.mapTypesLowTierMinTier + '+ Delirium', image: '/img/maps/map-delirium.png' },
      ]
    }
  }

  private updateVendoringExampleNames() {
    const otherClasses = STARTER_WEAPON_BASE_TYPES.filter(c => c.class !== this.filter.classPreset);
    this.vendoringRareStyle.exampleName = otherClasses[0]?.baseTypes[0] ?? 'Crude Bow';
    this.vendoringMagicStyle.exampleName = otherClasses[1]?.baseTypes[0] ?? 'Wooden Club';
  }

  private setExtraSettingsBasedOnFilterValues() {
    const cosmeticPresets = this.filter.cosmeticPresets;
    this.changeVisualStyle = cosmeticPresets.labelColor !== 'default' && cosmeticPresets.labelSize !== 'default';

    const restrictedUniquesTier = this.filter.uniqueTierCosmetics.find(t => t.uniqueTier === 'Restricted')?.displayOptions;
    this.bosses = restrictedUniquesTier?.active ?? true;

    this.isSpecificEndgameContentSelected = !this.filter.favouriteEndgameContent.includes('All');
    this.updateEndgameAllBossUniquesStatusLine();
    this.updateEndgamePresetEffectVisibility();
    this.updateFavouriteBossUniquesToggleVisibility();

    this.showCheapUniques = !this.filter.hideAllRemainingUniques;

    this.chanceBases = this.filter.chanceBaseTierCosmetics.find(t => t.chanceBaseTier === 'Searching')?.displayOptions?.active === true;

    this.showTransparentGold = !!(this.filter.goldStyleOverride?.displayOptions?.active && this.filter.goldStyleOverride?.displayOptions?.cosmeticOptions?.backgroundColor);
    this.hideETier = this.filter.tierCosmetics.find(t => t.tier === Tier.EVisible)?.displayOptions?.hidden ?? false;

    if (poe1) {
      this.heist = this.filter.showHeistBlueprints === true || this.filter.showHeistEnchantedBlueprints === true;

      const forbiddenTomeCustomTier = this.filter.customCurrencyTiers.find(t => t.name === 'Forbidden Tome');
      this.tomes = forbiddenTomeCustomTier?.tier !== Tier.FHidden;

      this.shippingUniques = this.filter.showHighDustValueUniques;
    } else {
      const irradiatedTabletTier = this.filter.customCurrencyTiers.find(t => t.name.includes('Irradiated Tablet'));
      this.hideLowValueTablets = irradiatedTabletTier?.tier === Tier.FHidden;

      this.jewels = !this.filter.hideJewels || this.filter.hideJewelsOfRarity !== Rarity.Rare;
    }

    this.cosmeticPresetsService.regenerateRules(this.filter);

    this.setMapTypes();
  }

  setMapTypes() {
    this.selectedMapTypes = [];
    if (this.filter.mapTypesT16Normal) this.selectedMapTypes.push('T16 Normal');
    if (this.filter.mapTypesT16EightMod) this.selectedMapTypes.push('T16 8-Mod');
    if (this.filter.mapTypesT16Blighted) this.selectedMapTypes.push('T16 Blighted');
    if (this.filter.mapTypesT16Influenced) this.selectedMapTypes.push('T16 Influenced');
    if (this.filter.mapTypesT16Delirium) this.selectedMapTypes.push('T16 Delirium');
    if (this.filter.mapTypesT16MemoryInfluenced) this.selectedMapTypes.push('T16.5');
    if (this.filter.mapTypesT16MemoryInfluencedCorrupted) this.selectedMapTypes.push('T16.5 8-Mod');
    if (this.filter.mapTypesBlightRavaged) this.selectedMapTypes.push('Blight-Ravaged');
    if (this.filter.mapTypesBlightRavagedCorrupted) this.selectedMapTypes.push('Blight-Ravaged 8-Mod');
    if (this.filter.mapTypesT17) this.selectedMapTypes.push('Nightmare');
    if (this.filter.mapTypesT17Corrupted) this.selectedMapTypes.push('Nightmare 8-Mod');
    // if (this.filter.mapTypesLowTierInfluenced) this.selectedMapTypes.push('T' + this.filter.mapTypesLowTierMinTier + '+ Influenced');
    if (this.filter.mapTypesLowTierMemoryInfluenced) this.selectedMapTypes.push('T' + this.filter.mapTypesLowTierMinTier + '+ Memory-Influenced');
    if (this.filter.mapTypesLowTierDelirium) this.selectedMapTypes.push('T' + this.filter.mapTypesLowTierMinTier + '+ Delirium');
    if (this.filter.mapTypesLowTierBlighted) this.selectedMapTypes.push('T' + this.filter.mapTypesLowTierMinTier + '+ Blighted');
  }

  selectedMapTypesChanged(event: string[]) {
    this.selectedMapTypes = event;
    this.filter.mapTypesLowTierBlighted = this.isMapTypeSelected('T' + this.filter.mapTypesLowTierMinTier + '+ Blighted');
    // this.filter.mapTypesLowTierInfluenced = this.isMapTypeSelected('T' + this.filter.mapTypesLowTierMinTier + '+ Influenced');
    this.filter.mapTypesLowTierMemoryInfluenced = this.isMapTypeSelected('T' + this.filter.mapTypesLowTierMinTier + '+ Memory-Influenced');
    this.filter.mapTypesLowTierDelirium = this.isMapTypeSelected('T' + this.filter.mapTypesLowTierMinTier + '+ Delirium');
    this.filter.mapTypesT16Normal = this.isMapTypeSelected('T16 Normal');
    this.filter.mapTypesT16EightMod = this.isMapTypeSelected('T16 8-Mod');
    this.filter.mapTypesT16Blighted = this.isMapTypeSelected('T16 Blighted');
    this.filter.mapTypesT16Influenced = this.isMapTypeSelected('T16 Influenced');
    this.filter.mapTypesT16Delirium = this.isMapTypeSelected('T16 Delirium');
    this.filter.mapTypesT16MemoryInfluenced = this.isMapTypeSelected('T16.5');
    this.filter.mapTypesT16MemoryInfluencedCorrupted = this.isMapTypeSelected('T16.5 8-Mod');
    this.filter.mapTypesT17 = true;
    this.filter.mapTypesT17Corrupted = this.isMapTypeSelected('Nightmare 8-Mod');
    this.filter.mapTypesBlightRavaged = this.isMapTypeSelected('Blight-Ravaged');
    this.filter.mapTypesBlightRavagedCorrupted = this.isMapTypeSelected('Blight-Ravaged 8-Mod');

    this.filterService.updateFilter();
  }

  setPreset(preset: Presets) {
    const hadProgressPreset = !!this.filter.progressPreset;
    this.presetPreviewService.initiateProgressPresetChange(preset);
    this.setExtraSettingsBasedOnFilterValues();
    if (!hadProgressPreset) {
      if (this.presetPreviewService.pendingChange()) {
        this.pendingProgressScroll = true;
      } else if (this.filter.progressPreset) {
        this.startExpansionScroll();
      }
    }
  }

  toggleFavouriteEndgameContent(endgameContent: EndgameContent | 'All') {
    if (endgameContent === 'All') {
      this.filter.favouriteEndgameContent = ['All'];
    } else {
      const current = this.filter.favouriteEndgameContent;
      if (current.includes('All')) {
        this.filter.favouriteEndgameContent = [endgameContent];
      } else if (current.includes(endgameContent)) {
        const next = current.filter(c => c !== endgameContent);
        this.filter.favouriteEndgameContent = next.length === 0 ? ['All'] : next;
      } else {
        this.filter.favouriteEndgameContent = [...current, endgameContent];
      }
    }
    this.updateSelectedEndgameContentDetail();
    this.syncEndgameContentEffects();
    this.filterService.updateFilter();
    this.setExtraSettingsBasedOnFilterValues();
  }

  private syncEndgameContentEffects() {
    this.endgameContentPresetService.sync(this.filter);
  }

  private updateFavouriteBossUniquesToggleVisibility() {
    this.showFavouriteRestrictedBossUniquesToggle = this.isSpecificEndgameContentSelected && !this.bosses;
  }

  private updateEndgamePresetEffectVisibility() {
    this.showEndgamePresetFavouriteCurrencyToggle = this.isSpecificEndgameContentSelected
      && this.endgameContentPresetService.selectionHasFavouriteCurrencyEffect(this.filter);
    this.showEndgamePresetCustomRulesToggle = this.isSpecificEndgameContentSelected
      && this.endgameContentPresetService.selectionHasCustomRulesEffect(this.filter);
    this.endgamePresetCustomRuleDescriptions = this.endgameContentPresetService
      .getSelectedCustomRuleDescriptions(this.filter)
      .map(({ content, description }) => ({
        contentLabel: EndgameContentLabelMap[content],
        description,
      }));
  }

  toggleEndgamePresetPromoteFavouriteCurrency() {
    this.filter.endgamePresetPromoteFavouriteCurrency = !this.filter.endgamePresetPromoteFavouriteCurrency;
    this.syncEndgameContentEffects();
    this.filterService.updateFilter();
    this.updateSelectedEndgameContentDetail();
  }

  toggleEndgamePresetEnableCustomRules() {
    this.filter.endgamePresetEnableCustomRules = !this.filter.endgamePresetEnableCustomRules;
    this.syncEndgameContentEffects();
    this.filterService.updateFilter();
    this.updateSelectedEndgameContentDetail();
  }

  private updateEndgameAllBossUniquesStatusLine() {
    const restrictedActive = this.filter.uniqueTierCosmetics.find(t => t.uniqueTier === 'Restricted')?.displayOptions?.active ?? true;
    const hideRemaining = this.filter.hideAllRemainingUniques;

    if (restrictedActive) {
      this.endgameAllBossUniquesStatusLine = `Highlighting restricted boss uniques`;
    } else if (hideRemaining) {
      this.endgameAllBossUniquesStatusLine = `Hiding restricted boss uniques`;
    } else {
      this.endgameAllBossUniquesStatusLine = `Restricted boss uniques visible but not highlighted`;
    }
  }

  private updateSelectedEndgameContentDetail() {
    if (this.filter.favouriteEndgameContent.includes('All')) {
      this.endgameContentSelectedDetail = undefined;
      this.updateEndgamePresetEffectVisibility();
      return;
    }

    const selected = this.filter.favouriteEndgameContent.filter((c): c is EndgameContent => c !== 'All');
    const contentLine = selected.map(c => EndgameContentLabelMap[c]).join(', ');

    const allItemTypesMap = new Map<string, { itemType: string; image?: string }>();
    for (const content of selected) {
      for (const item of this.itemDataService.getItemTypesByContent(content)) {
        if (!allItemTypesMap.has(item.itemType)) {
          allItemTypesMap.set(item.itemType, {
            itemType: item.itemType,
            image: item.image ? this.imgPrefix + item.image : undefined,
          });
        }
      }
    }
    const allItemTypes = [...allItemTypesMap.values()];
    const itemTypes = allItemTypes.slice(0, 3);
    const moreItemTypesText = allItemTypes.length > 3 ? `and ${allItemTypes.length - 3} others` : undefined;

    const bossUniquesDetail = this.buildBossUniquesDetail(selected);
    const uniques = bossUniquesDetail.uniques;
    const moreUniquesText = bossUniquesDetail.moreUniquesText;
    const moreBossesText = bossUniquesDetail.moreBossesText;

    this.endgameContentSelectedDetail = {
      contentLine,
      itemTypes,
      moreItemTypesText,
      promotedLabel: bossUniquesDetail.promotedLabel,
      uniques,
      moreUniquesText,
      moreBossesText,
    };
    this.updateEndgamePresetEffectVisibility();
  }

  showEndgameContentInfo(content: EndgameContent | 'All') {
    if (content === 'All') {
      this.endgameContentHoverDetail = undefined;
      this.endgameContentHoveringAll = true;
    } else {
      this.endgameContentHoveringAll = false;
      this.endgameContentHoverDetail = this.buildEndgameContentDetail(content);
    }
  }

  hideEndgameContentInfo() {
    this.endgameContentHoverDetail = undefined;
    this.endgameContentHoveringAll = false;
  }

  private buildEndgameContentDetail(content: EndgameContent): EndgameContentInfoDetail {
    const allItemTypes = this.itemDataService.getItemTypesByContent(content).map(item => ({
      itemType: item.itemType,
      image: item.image ? this.imgPrefix + item.image : undefined,
    }));

    let moreItemTypesText: string | undefined;
    const itemTypes = allItemTypes.slice(0, 3);
    if (allItemTypes.length > 3) {
      moreItemTypesText = `and ${allItemTypes.length - 3} more item types`;
    }

    const bossUniquesDetail = this.buildBossUniquesDetail([content]);

    return {
      contentLine: EndgameContentLabelMap[content],
      itemTypes,
      moreItemTypesText,
      promotedLabel: bossUniquesDetail.promotedLabel,
      uniques: bossUniquesDetail.uniques,
      moreUniquesText: bossUniquesDetail.moreUniquesText,
      moreBossesText: bossUniquesDetail.moreBossesText,
    };
  }

  private buildBossUniquesDetail(contents: EndgameContent[]): {
    promotedLabel?: string;
    uniques: { name: string; image?: string }[];
    moreUniquesText?: string;
    moreBossesText?: string;
  } {
    const bossesWithSortedUniques = contents
      .flatMap(content => this.itemDataService.getUniqueBossDropEntries(content))
      .reduce((acc, entry) => {
        const existing = acc.find(e => e.boss === entry.boss);
        const uniques = [...new Set(entry.baseTypes)];
        if (existing) {
          existing.uniques.push(...uniques.filter(name => !existing.uniques.includes(name)));
        } else {
          acc.push({ boss: entry.boss, uniques });
        }
        return acc;
      }, [] as { boss: string; uniques: string[] }[])
      .map(entry => ({
        boss: entry.boss,
        uniques: this.sortUniquesByValue(entry.uniques),
      }))
      .filter(entry => entry.uniques.length > 0)
      .sort((a, b) => this.getUniqueSortValue(b.uniques[0]) - this.getUniqueSortValue(a.uniques[0]));

    const totalUniques = bossesWithSortedUniques.reduce((sum, b) => sum + b.uniques.length, 0);

    if (bossesWithSortedUniques.length === 0) {
      return { uniques: [] };
    }

    if (bossesWithSortedUniques.length === 1) {
      const boss = bossesWithSortedUniques[0];
      return {
        promotedLabel: `${boss.boss} uniques:`,
        uniques: boss.uniques.slice(0, 2).map(name => this.mapUniqueDisplay(name)),
        moreUniquesText: boss.uniques.length > 2 ? `and ${boss.uniques.length - 2} more uniques` : undefined,
      };
    }

    const bestBoss = bossesWithSortedUniques[0];
    const otherBossCount = bossesWithSortedUniques.length - 1;
    return {
      promotedLabel: `${bestBoss.boss} uniques:`,
      uniques: bestBoss.uniques.slice(0, 2).map(name => this.mapUniqueDisplay(name)),
      moreBossesText: `and ${otherBossCount} more boss${otherBossCount === 1 ? '' : 'es'} totalling ${totalUniques} uniques`,
    };
  }

  private sortUniquesByValue(names: string[]): string[] {
    return [...names].sort((a, b) => this.getUniqueSortValue(b) - this.getUniqueSortValue(a));
  }

  private getUniqueSortValue(name: string): number {
    return this.economyService.getUniqueValue(name)?.price ?? 0;
  }

  private mapUniqueDisplay(name: string): { name: string; image?: string } {
    const unique = this.uniqueByName.get(name);
    return {
      name,
      image: unique?.image ? '/img/uniques/' + unique.image : undefined,
    };
  }

  previewProgressPreset(preset: Presets) {
    this.presetPreviewService.previewProgressPresetChange(preset);
  }

  setClassPreset(classPreset: string) {
    const hadClassPreset = !!this.filter.classPreset;
    this.presetPreviewService.initiateClassPresetChange(classPreset);
    this.updateVendoringExampleNames();
    if (!hadClassPreset) {
      this.startExpansionScroll();
    }
  }

  previewClassPreset(classPreset: string) {
    this.presetPreviewService.previewClassPresetChange(classPreset);
  }

  updateEconomy() {
    if (this.filter.ssf || this.filter.ruthless || FORCE_FIXED_ECO) this.filter.economy = Economy.Fixed;
    else if (this.filter.hardcore) this.filter.economy = this.filter.league ? Economy.LeagueHardcore : Economy.StandardHardcore;
    else this.filter.economy = this.filter.league ? Economy.League : Economy.Standard;

    this.presetsService.setProgressPreset(this.filter.progressPreset ?? 'presetEarlyCampaign1');
  }

  toggleSsf() {
    this.presetsService.toggleSsf();
    this.updateEconomy();
    this.setExtraSettingsBasedOnFilterValues();
  }

  toggleRuthless() {
    this.filter.ruthless = !this.filter.ruthless;
    this.updateEconomy();

    if (this.filter.progressPreset) {
      this.setPreset(this.filter.progressPreset);
    }

    this.setExtraSettingsBasedOnFilterValues();
  }

  toggleChangeVisualStyle() {
    this.changeVisualStyle = !this.changeVisualStyle;
    if (this.changeVisualStyle) {
      this.filter.cosmeticPresets.labelColor = 'accentTiered';
      this.filter.cosmeticPresets.labelSize = 'larger';
    } else {
      this.filter.cosmeticPresets.labelColor = 'default';
      this.filter.cosmeticPresets.labelSize = 'default';
    }
    this.cosmeticPresetsService.regenerateRules(this.filter);
    this.cosmeticPresetsService.applyPreset(this.filter);
    this.filterService.updateFilter();
  }

  toggleBosses() {
    this.bosses = !this.bosses;
    const restrictedUniquesTier = this.filter.uniqueTierCosmetics.find(t => t.uniqueTier === 'Restricted')?.displayOptions;
    if (!restrictedUniquesTier) return;

    if (this.filter.progressPreset === 'presetMidMaps70' || this.filter.progressPreset === 'presetEndGame80' || this.filter.progressPreset === 'presetStrict90' || this.filter.progressPreset === 'presetVeryStrict95') {
      restrictedUniquesTier.active = this.bosses;
    } else {
      restrictedUniquesTier.active = true;
    }

    this.updateEndgameAllBossUniquesStatusLine();
    this.updateFavouriteBossUniquesToggleVisibility();
    this.filterService.updateFilter();
  }

  toggleShowFavouriteRestrictedBossUniques() {
    this.filter.showFavouriteRestrictedBossUniques = !this.filter.showFavouriteRestrictedBossUniques;
    this.filterService.updateFilter();
  }

  toggleShowCheapUniques() {
    this.showCheapUniques = !this.showCheapUniques;

    this.filter.hideAllRemainingUniques = !this.showCheapUniques;

    const otherUniquesTier = this.filter.uniqueTierCosmetics.find(t => t.uniqueTier === 'Other')?.displayOptions;
    if (otherUniquesTier) {
      otherUniquesTier.active = false;
    }

    this.updateEndgameAllBossUniquesStatusLine();
    this.filterService.updateFilter();
  }

  toggleHeist() {
    this.heist = !this.heist;
    this.filter.showHeistEnchantedBlueprints = this.heist;
    this.filter.showHeistEnchantedContracts = this.heist;
    this.filter.showHeistBlueprints = this.heist;
    this.filter.showHeistContracts = this.heist;
    this.filter.showRoguesMarkers = this.heist;
    this.filterService.updateFilter();
  }

  toggleChanceBases() {
    this.chanceBases = !this.chanceBases;
    const searchingChanceBasesTier = this.filter.chanceBaseTierCosmetics.find(t => t.chanceBaseTier === 'Searching');
    if (searchingChanceBasesTier) {
      searchingChanceBasesTier.displayOptions.active = this.chanceBases;
    }
    this.filterService.updateFilter();
  }

  toggleTomes() {
    this.tomes = !this.tomes;
    const forbiddenTomeCustomTier = this.filter.customCurrencyTiers.find(t => t.name === 'Forbidden Tome');
    if (this.tomes && forbiddenTomeCustomTier?.tier === Tier.FHidden) {
      this.filterService.resetCurrencyItemTier('Forbidden Tome');
    } else if (!this.tomes && forbiddenTomeCustomTier?.tier !== Tier.FHidden) {
      this.filterService.updateCurrencyItemTier('Forbidden Tome', Tier.FHidden);
    }
  }

  toggleLowValueTablets() {
    this.hideLowValueTablets = !this.hideLowValueTablets;
    this.presetsService.enableLowValueTablets(this.filter, !this.hideLowValueTablets);
  }

  toggleShippingUniques() {
    this.shippingUniques = !this.shippingUniques;
    this.filter.showHighDustValueUniques = this.shippingUniques;
    this.filterService.updateFilter();
  }

  toggleJewels() {
    this.jewels = !this.jewels;
    this.filter.hideJewels = !this.jewels;
    this.filter.hideJewelsOfRarity = !this.jewels ? Rarity.Rare : this.filter.hideJewelsOfRarity;
    this.filterService.updateFilter();
  }

  toggleShowAllIdentifiedItems() {
    this.filter.showAllIdentifiedItems = !this.filter.showAllIdentifiedItems;
    this.filter.showIdentifiedGear = !this.filter.showAllIdentifiedItems;
    this.filter.showIdentifiedJewellery = !this.filter.showAllIdentifiedItems;
    this.filter.showIdentifiedJewels = !this.filter.showAllIdentifiedItems;
    this.filter.showIdentifiedCustomItems = !this.filter.showAllIdentifiedItems;
    this.filter.hideRemainingIdentifiedItems = !this.filter.showAllIdentifiedItems;

    this.filterService.updateFilter();
  }

  toggleHideCurrency = () => {
    this.filter.hideCurrency = !this.filter.hideCurrency;
    this.rebuildCurrencyTiers();
  }

  rebuildCurrencyTiers() {
    this.currencyTierService.combineCurrencyTierListData(this.filter);
    this.updateFilter();
  }

  toggleHideETier() {
    this.hideETier = !this.hideETier;
    const eTier = this.filter.tierCosmetics.find(t => t.tier === Tier.EVisible);
    if (eTier) {
      eTier.displayOptions.hidden = this.hideETier;
    }
    this.filterService.updateFilter();
  }

  toggleShowTransparentGold() {
    this.showTransparentGold = !this.showTransparentGold;
    if (!this.filter.goldStyleOverride?.displayOptions?.cosmeticOptions) {
      this.filter.goldStyleOverride = new ItemTypeOverride({
        name: 'Gold',
        displayOptions: {
          active: true,
          cosmeticOptions: { minimapIconShape: MinimapIconShape.Star },
        },
      });
    }
    const cosmeticOptions = this.filter.goldStyleOverride.displayOptions.cosmeticOptions!;
    if (this.showTransparentGold) {
      cosmeticOptions.backgroundColor = '#000000aa';
    } else {
      cosmeticOptions.backgroundColor = undefined;
    }
    this.filterService.updateFilter();
  }

  toggleCustomizeLabelStyles() {
    const expanding = !this.showCosmeticPresets;
    this.showCosmeticPresets = expanding;
    if (expanding) {
      this.startExpansionScroll();
    }
  }

  toggleInstallationInfo() {
    const expanding = !this.installationInfo;
    this.installationInfo = expanding;
    if (expanding) {
      this.startExpansionScroll();
    }
  }

  isMapTypeSelected(mapType: string) {
    return this.selectedMapTypes.includes('All') || this.selectedMapTypes.includes(mapType);
  }

  updateFilter() {
    this.filterService.updateFilter();
  }

  showPresetTooltip(preset: Presets) {
    this.overridePresetTooltip = (poe1 && preset === 'presetMidMaps70' && !this.filter.ssf)
      ? this.presetsService.poe1progressAtlasNonSsfPresetText
      : this.presetsService.levelPresetTexts[preset];
  }

  hidePresetTooltip() {
    this.overridePresetTooltip = undefined;
  }

  showClassPresetTooltip(classPreset: string) {
    this.overrideClassPresetTooltip = this.classPresetTexts[classPreset];
  }

  hideClassPresetTooltip() {
    this.overrideClassPresetTooltip = undefined;
  }

  setLabelColor(color: 'default' | 'accentNeutral' | 'accentTiered') {
    this.cosmeticPresetsService.setLabelColor(color, this.filter);
    this.updateStartingTierSliderVisibility();
    this.cosmeticPresetsService.applyPreset(this.filter);
    this.filterService.updateFilter();
  }

  setLabelSize(size: 'default' | 'larger' | 'veryLarge') {
    this.cosmeticPresetsService.setLabelSize(size, this.filter);
    this.updateStartingTierSliderVisibility();
    this.cosmeticPresetsService.applyPreset(this.filter);
    this.filterService.updateFilter();
  }

  updateStartingTierDisplay() {
    const tier = this.filter.cosmeticPresets.startingTier;
    this.startingTierIndex = STARTING_TIER_OPTIONS.indexOf(tier);
    if (this.startingTierIndex < 0) {
      this.startingTierIndex = 2;
    }
    this.startingTierLabel = STARTING_TIER_LABELS[STARTING_TIER_OPTIONS[this.startingTierIndex]];
  }

  updateStartingTierSliderVisibility() {
    this.showStartingTierSlider = this.filter.cosmeticPresets.labelColor !== 'default' || this.filter.cosmeticPresets.labelSize !== 'default';
  }

  onStartingTierChange(event: Event) {
    const index = Number((event.target as HTMLInputElement).value);
    this.startingTierIndex = index;
    this.startingTierLabel = STARTING_TIER_LABELS[STARTING_TIER_OPTIONS[index]];
    this.cosmeticPresetsService.setStartingTier(STARTING_TIER_OPTIONS[index], this.filter);
    this.cosmeticPresetsService.applyPreset(this.filter);
    this.filterService.updateFilter();
  }

  updateDefaultLabelSizeDisplay() {
    const size = this.filter.cosmeticPresets.defaultLabelSize;
    this.defaultLabelSizeIndex = DEFAULT_LABEL_SIZE_OPTIONS.indexOf(size);
    if (this.defaultLabelSizeIndex < 0) {
      this.defaultLabelSizeIndex = 3;
    }
    this.defaultLabelSizeLabel = DEFAULT_LABEL_SIZE_LABELS[DEFAULT_LABEL_SIZE_OPTIONS[this.defaultLabelSizeIndex]];
  }

  onDefaultLabelSizeChange(event: Event) {
    const index = Number((event.target as HTMLInputElement).value);
    this.setDefaultLabelSize(DEFAULT_LABEL_SIZE_OPTIONS[index]);
  }

  setDefaultLabelSize(size: DefaultLabelSize) {
    this.cosmeticPresetsService.setDefaultLabelSize(size, this.filter);
    this.updateDefaultLabelSizeDisplay();
    this.cosmeticPresetsService.applyPreset(this.filter);
    this.filterService.updateFilter();
  }

  toggleSearchedGems() {
    this.filter.showSearchedGems = !this.filter.showSearchedGems;
    this.filterService.updateFilter();
  }

  toggleShowVendoringRares() {
    this.filter.showVendoringRares = !this.filter.showVendoringRares;
    if (!this.filter.showVendoringRares) {
      this.filter.showVendoringMagics = false;
    }
    this.filterService.updateFilter();
  }

  toggleShowVendoringMagics() {
    this.filter.showVendoringMagics = !this.filter.showVendoringMagics;
    this.filterService.updateFilter();
  }

  importGems(gems: BaseType[]) {
    this.filter.showSearchedGems = true;
    this.filter.showSearchedGemTypes = [...new Set([...this.filter.showSearchedGemTypes.filter(g => g !== 'None'), ...gems.map(g => g.name)])];
    if (this.filter.showSearchedGemTypes.length === 0) {
      this.filter.showSearchedGemTypes = ['None'];
    }
    this.updateFilter();
  }

  toggleShowLevelingGear = () => {
    this.filter.showLevelingGear = !this.filter.showLevelingGear;
    this.filter.showLevelingGearWeapons = this.filter.showLevelingGear;
    this.filter.showLevelingGearArmour = this.filter.showLevelingGear;
    this.updateFilter();
  }

  levelingWeaponClassesChanged(newValues: string[]) {
    this.filter.showLevelingGearWeaponClasses = newValues;
    this.updateFilter();
  }

  toggleShowLevelingGearArmour = () => {
    this.filter.showLevelingGearArmour = !this.filter.showLevelingGearArmour;
    this.updateFilter();
  }

  levelingArmourDefenceTypesChanged(newValues: string[]) {
    this.filter.showLevelingGearArmourDefenceTypes = newValues;
    this.updateFilter();
  }

  scrollToHowToUseThisWebsite() {
    document.getElementById('how-to-use-this-website')?.scrollIntoView({ behavior: 'smooth' });
  }

  private startExpansionScroll(): void {
    requestAnimationFrame(() => this.scrollWithSectionExpansion());
  }

  private scrollWithSectionExpansion(): void {
    if (this.scrollAnimationFrame) {
      cancelAnimationFrame(this.scrollAnimationFrame);
    }

    const startY = window.pageYOffset;
    const startTime = performance.now();

    const step = (now: number) => {
      const progress = Math.min((now - startTime) / this.sectionTransitionMs, 1);
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      window.scrollTo({ top: startY + this.expansionScrollDistance * eased });

      if (progress < 1) {
        this.scrollAnimationFrame = requestAnimationFrame(step);
      } else {
        this.scrollAnimationFrame = undefined;
      }
    };

    this.scrollAnimationFrame = requestAnimationFrame(step);
  }

  copyGameFolderLinkToClipboard(event: any) {
    event.preventDefault();
    event.stopPropagation();
    navigator.clipboard.writeText(`%userprofile%\\Documents\\My Games\\${this.gameName}`);
  }

  daynamicWaystoneLevelChanged() {
    if (this.filter.dynamicWaystonesLevel && this.filter.dynamicWaystonesLevel > (this.poe2 ? 15 : 16)) {
      this.filter.dynamicWaystonesLevel = this.poe2 ? 15 : 16;
    }
    this.updateFilter();
  }

  toggleAllOtherGear() {
    this.filter.hideAllOtherGear = !this.filter.hideAllOtherGear;

    this.updateFilter();
  }

  toggleSupportGems() {
    this.filter.hideUncutSupportGemsBelowLevel = this.filter.hideUncutSupportGemsBelowLevel === 5 ? null : 5;
    if (this.filter.hideUncutSupportGemsBelowLevel === 5 && !this.filter.hideUncutGems) {
      this.filter.hideUncutGems = true;
    }

    this.currencyTierService.combineCurrencyTierListData(this.filter);
    this.updateFilter();
  }

  levelingWeaponClassesChangedPoE2(newValues: string[]) {
    this.filter.showLevelingGearWeaponClasses = [...newValues];

    if (newValues.includes(WeaponType.All)) {
      newValues = this.itemDataService.getAllWeaponTypes();
    }

    // There should be at most 2 rows: one for 'regular' weapon types, and one for 'special' weapon types.
    // Divide anything in newValues over these two rows, and remove the empty row if needed.
    // If there is only one of both and we'd need both, then clone the other row.

    const specialWeaponTypes = PresetsService.specialWeaponTypes;
    const presetWeaponFilters = this.filter.weaponFilters.filter(w => w.fromProgressPreset || w.fromClassPreset);

    // Divide newValues into regular and special types
    const regularTypes = newValues.filter(v => !specialWeaponTypes.includes(v as WeaponType)) as WeaponType[];
    const specialTypes = newValues.filter(v => specialWeaponTypes.includes(v as WeaponType)) as WeaponType[];

    // Find existing rows (at most 1 of each type)
    const existingSpecialFilter = presetWeaponFilters.find(w =>
      w.weaponType.some(wt => specialWeaponTypes.includes(wt))
    );
    const existingRegularFilter = presetWeaponFilters.find(w =>
      !w.weaponType.some(wt => specialWeaponTypes.includes(wt))
    );

    // Capture properties from existing filters before removing them
    const specialFilterProps = existingSpecialFilter ? {
      fromProgressPreset: existingSpecialFilter.fromProgressPreset,
      fromClassPreset: existingSpecialFilter.fromClassPreset,
      show: existingSpecialFilter.show,
      rarity: existingSpecialFilter.rarity,
      itemLevel: existingSpecialFilter.itemLevel,
      unidentifiedTier: existingSpecialFilter.unidentifiedTier,
      highlightConfig: structuredClone(existingSpecialFilter.highlightConfig)
    } : null;

    const regularFilterProps = existingRegularFilter ? {
      fromProgressPreset: existingRegularFilter.fromProgressPreset,
      fromClassPreset: existingRegularFilter.fromClassPreset,
      show: existingRegularFilter.show,
      rarity: existingRegularFilter.rarity,
      itemLevel: existingRegularFilter.itemLevel,
      unidentifiedTier: existingRegularFilter.unidentifiedTier,
      highlightConfig: structuredClone(existingRegularFilter.highlightConfig)
    } : null;

    // Remove all preset weapon filters first (we'll recreate what we need)
    presetWeaponFilters.forEach(filter => {
      const index = this.filter.weaponFilters.indexOf(filter);
      if (index !== -1) {
        this.filter.weaponFilters.splice(index, 1);
      }
    });

    // Create or update regular row if needed
    if (regularTypes.length > 0) {
      const gearSettings = this.presetHelperService.initializeMyGearSettingsByProgress(this.filter);
      const baseProps = regularFilterProps || specialFilterProps || {
        fromProgressPreset: true,
        fromClassPreset: false,
        show: true
      };

      const regularFilter = new WeaponFilter({
        ...baseProps,
        weaponType: regularTypes,
        baseTypeTier: gearSettings.baseTypeTier,
        rarity: gearSettings.rarity,
        itemLevel: gearSettings.itemLevel,
        unidentifiedTier: gearSettings.minTier,
        highlightConfig: structuredClone(gearSettings.highlightConfig)
      });
      this.filter.weaponFilters.push(regularFilter);
    }

    // Create or update special row if needed
    if (specialTypes.length > 0) {
      const gearSettings = this.presetHelperService.initializeMyGearSettingsByProgress(this.filter);
      const baseProps = specialFilterProps || regularFilterProps || {
        fromProgressPreset: true,
        fromClassPreset: false,
        show: true
      };

      const specialFilter = new WeaponFilter({
        ...baseProps,
        weaponType: specialTypes,
        baseTypeTier: BaseTypeTier.Basic, // Special types always have Basic tier
        rarity: gearSettings.rarity,
        itemLevel: gearSettings.itemLevel,
        unidentifiedTier: gearSettings.minTier,
        highlightConfig: structuredClone(gearSettings.highlightConfig)
      });
      this.filter.weaponFilters.push(specialFilter);
    }

    this.updateFilter();
  }

  levelingArmourDefenceTypesChangedPoE2(newValues: string[]) {
    this.filter.showLevelingGearArmourDefenceTypes = newValues;

    if (newValues.includes(DefenceType.All)) {
      newValues = this.itemDataService.getAllDefenceTypes().map(d => d.name);
    }

    this.filter.armourFilters.filter(a => a.fromProgressPreset || a.fromClassPreset).forEach((armourFilter) => {
      if ([ArmourType.Bucklers].includes(armourFilter.armourType[0])) return;
      armourFilter.defenceType = newValues as DefenceType[];
    });
    this.updateFilter();
  }

  toggleShowLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes() {
    this.filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes = !this.filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes;
    this.updateFilter();
  }

  hasCasterWeaponSelected(): boolean {
    return this.filter.showLevelingGearWeaponClasses?.some(wc => this.casterWeaponTypes.includes(wc as WeaponType)) ?? false;
  }

  hasQuiverSelected(): boolean {
    return this.filter.showLevelingGearWeaponClasses?.some(wc => wc === WeaponType.Quivers) ?? false;
  }

  getOtherUniquesThreshold(): string {
    const uniqueTieringMode = this.filter.uniquesTieringMode;
    let threshold;
    if (uniqueTieringMode === 'Custom') {
      threshold = this.filter.uniquesTieringModeCustom.find(t => t.tier === 'Good');
    } else {
      threshold = this.uniquesTierService.getTierCutoffValues().find(t => t.mode === uniqueTieringMode)?.values.find(v => v.tier === 'Good');
    }
    return (threshold ? `${formatTierThresholdValue(threshold)} ${threshold.baseCurrency ?? 'Exalted Orb'}` : '10 Exalted Orb') + 's';
  }
}
